import React from "react";
import { motion } from "framer-motion";
import { Leaf, Target, Eye, Award } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { PageHero, SectionHeader } from "@/components/PageBits";
import { useQuote } from "@/context/QuoteContext";
import { Button } from "@/components/ui/button";

const VALUES = [
  { icon: Award, t: "Uncompromising Quality", d: "Every batch is tested for aroma, strength and purity before it reaches you." },
  { icon: Leaf, t: "Rooted in Nature", d: "We source responsibly from India's finest tea gardens." },
  { icon: Target, t: "Consistency First", d: "The same perfect cup, every single time — at any scale." },
];

export default function About() {
  useSEO({ title: "About Us", description: "Leaf Alchemy delivers premium-quality tea powder with exceptional taste and consistency for tea professionals and households across India." });
  const { openQuote } = useQuote();

  return (
    <div data-testid="about-page">
      <PageHero eyebrow="About Leaf Alchemy" title="Crafted by Nature. Perfected for Every Cup."
        subtitle="A premium Indian tea brand built on quality, consistency and a love for the perfect brew." />

      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <img src="https://images.unsplash.com/photo-1714125953083-c363fb45696b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Fresh tea leaves" className="rounded-2xl w-full h-[500px] object-cover shadow-xl" loading="lazy" />
          </Reveal>
          <Reveal delay={0.15}>
            <span className="text-olive text-xs font-semibold tracking-[0.25em] uppercase">Our Story</span>
            <h2 className="font-display text-4xl text-forest mt-4 leading-tight">A Passion for the Perfect Brew</h2>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              Leaf Alchemy is committed to delivering premium-quality tea powder with exceptional taste and
              consistency. We carefully source quality tea leaves and blend them to create rich flavors loved
              by tea professionals and households alike.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Whether serving thousands of customers in restaurants or brewing tea at home, Leaf Alchemy
              guarantees freshness, aroma and satisfaction. Our blends are the result of careful sourcing,
              rigorous testing and the craft of experienced tea blenders.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 bg-forest text-cream">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-2 gap-10">
          <Reveal>
            <div className="flex gap-5" data-testid="mission-block">
              <div className="w-14 h-14 rounded-2xl bg-gold/20 grid place-items-center text-gold shrink-0"><Target size={26} /></div>
              <div>
                <h3 className="font-display text-2xl text-gold">Our Mission</h3>
                <p className="text-cream/80 mt-2">Deliver premium tea experiences through quality, consistency and innovation.</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex gap-5" data-testid="vision-block">
              <div className="w-14 h-14 rounded-2xl bg-gold/20 grid place-items-center text-gold shrink-0"><Eye size={26} /></div>
              <div>
                <h3 className="font-display text-2xl text-gold">Our Vision</h3>
                <p className="text-cream/80 mt-2">Become India&rsquo;s most trusted premium tea powder brand.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader eyebrow="Our Values" title="What We Stand For" center />
          <div className="grid md:grid-cols-3 gap-8">
            {VALUES.map((v, i) => (
              <Reveal key={v.t} delay={i * 0.08}>
                <div className="bg-cream border border-forest/10 rounded-2xl p-8 h-full hover:border-gold/40 transition-colors">
                  <v.icon className="text-forest mb-4" size={30} />
                  <h3 className="font-display text-xl text-forest">{v.t}</h3>
                  <p className="text-sm text-muted-foreground mt-2">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="text-center mt-14">
            <Button onClick={() => openQuote()} className="rounded-full bg-forest hover:bg-olive text-cream px-8 h-12 transition-colors" data-testid="about-cta">Partner With Us</Button>
          </div>
        </div>
      </section>
    </div>
  );
}
